/**
 * RefeditToolbar — the in-canvas REFEDIT control strip (Phase FIN.1).
 *
 * Shown only while the active drawing space has kind === "refedit". The
 * toolbar surfaces the REFSET and REFCLOSE workflow so the user can:
 *   - Add the currently selected entities to the working set
 *   - Remove the currently selected entities from the working set
 *   - Save the in-place edit and close the session (REFCLOSE save)
 *   - Discard the in-place edit and restore the original block
 *
 * Layout: a single horizontal flex strip pinned to the top of the
 * drawing area when REFEDIT is live. Mirrors AutoCAD's REFEDIT toolbar
 * minimalism — no popovers, no hidden submenus, every action is a single
 * click.
 *
 * Routing: every button dispatches the corresponding command via
 * `useEditorStore.dispatch`. The reducer handles the heavy lifting
 * (snapshot save/discard, working-set diffs).
 *
 * Per CLAUDE.md: <300 LOC, dual-language strings via useT().
 */

import type { ReactElement } from "react";

import { useT } from "../i18n";
import { selectActiveSpace, useEditorStore } from "../state/editorStore";

export function RefeditToolbar(): ReactElement | null {
  const t = useT();
  const state = useEditorStore((store) => store.state);
  const dispatch = useEditorStore((store) => store.dispatch);
  const activeSpace = selectActiveSpace(state);

  if (activeSpace.kind !== "refedit") {
    return null;
  }

  const blockName = activeSpace.refeditBlockName ?? "";
  const workingSet = activeSpace.refeditWorkingSet ?? [];
  const selection = state.selection.entityIds;

  const canAdd =
    selection.length > 0 && selection.some((id) => !workingSet.includes(id));
  const canRemove =
    selection.length > 0 && selection.some((id) => workingSet.includes(id));

  return (
    <div
      className="refedit-toolbar"
      data-testid="refedit-toolbar"
      role="toolbar"
      aria-label={t("refedit.toolbar.title")}
    >
      <span className="refedit-toolbar-status" data-testid="refedit-status">
        {t("status.refedit.editing", { name: blockName })}
      </span>
      <span
        className="refedit-toolbar-count"
        data-testid="refedit-working-count"
      >
        {t("refedit.working_set_count", { count: workingSet.length })}
      </span>
      <button
        type="button"
        className="refedit-toolbar-btn"
        data-testid="refedit-add-to-set"
        disabled={!canAdd}
        onClick={() => {
          const toAdd = selection.filter((id) => !workingSet.includes(id));
          if (toAdd.length === 0) return;
          dispatch({
            type: "space/refedit-add-to-working-set",
            source: "scene",
            entityIds: toAdd,
          });
        }}
      >
        {t("refedit.add_to_set")}
      </button>
      <button
        type="button"
        className="refedit-toolbar-btn"
        data-testid="refedit-remove-from-set"
        disabled={!canRemove}
        onClick={() => {
          const toRemove = selection.filter((id) => workingSet.includes(id));
          if (toRemove.length === 0) return;
          dispatch({
            type: "space/refedit-remove-from-working-set",
            source: "scene",
            entityIds: toRemove,
          });
        }}
      >
        {t("refedit.remove_from_set")}
      </button>
      <button
        type="button"
        className="refedit-toolbar-btn refedit-toolbar-btn-primary"
        data-testid="refedit-save"
        onClick={() => {
          dispatch({
            type: "space/exit-refedit",
            source: "scene",
            action: "save",
          });
        }}
      >
        {t("refedit.save")}
      </button>
      <button
        type="button"
        className="refedit-toolbar-btn refedit-toolbar-btn-discard"
        data-testid="refedit-discard"
        onClick={() => {
          dispatch({
            type: "space/exit-refedit",
            source: "scene",
            action: "discard",
          });
        }}
      >
        {t("refedit.discard")}
      </button>
    </div>
  );
}
